import { useLazyQuery, useQuery } from '@apollo/client';
import { Button, Icon, Loader } from '@teamsnap/teamsnap-ui';
import { setDate, setHours, setMinutes } from 'date-fns';
import { isNil, times } from 'lodash';
import * as React from 'react';

// Local imports
import { AuthContext } from 'auth';
import PaginatedTable from 'components/common/table/paginated-table';
import { getAbbreviatedTimezone } from 'core/utils';
import { getData, storeCalendarPage } from 'stores/session-storage';
import CalendarExportModal from './calendar-export-modal';
import CalendarFilters from './calendar-filters';
import {
  Filters,
  eventSources,
  getEventCards,
  getMonthHeader,
  getWeek,
  groupEvents,
} from './calendar-utils';
import Event, { EventModal, GroupModal, GroupedEvents } from './event';

// GraphQL
import CalendarEventsQuery, {
  EventGQLType,
} from 'core/gql/queries/calendar-events';
import DivisionsAndTeamsQuery, {
  CalendarEventDivisions,
} from 'core/gql/queries/calendar-events-divisions-and-teams';
import EventSeasonsQuery, {
  CalendarEventSeason,
} from 'core/gql/queries/calendar-events-seasons';
import CalendarLocationsQuery, {
  CalendarLocation,
} from 'core/gql/queries/calendar-locations';

import './calendar.scss';

enum WeekDay {
  Sun,
  Mon,
  Tue,
  Wed,
  Thu,
  Fri,
  Sat,
}

const HeaderColumn = ({ date }: { date: Date }) => (
  <div className="calendar-col">
    <span className="weekday">{WeekDay[date.getDay()]}</span>
    <div
      className={`day${
        date.toDateString() === TODAY.toDateString() ? ' active' : ''
      }`}
    >
      <span>{date.getDate()}</span>
    </div>
  </div>
);

const defaultRows = times(18, Number).map((index) => ({
  hour: index + 6,
  period: index < 6 ? 'am' : 'pm',
  label: index < 6 ? index + 6 : index - 6 || 12,
}));

const fullDayRows = times(24, Number).map((index) => ({
  hour: index,
  period: index < 12 ? 'am' : 'pm',
  label: index > 12 ? index - 12 : index || 12,
}));

const TODAY = new Date();
const MONDAY = setDate(TODAY, TODAY.getDate() - (TODAY.getDay() || 7) + 1);

const OrgCalendar = ({ organizationId }: { organizationId: string }) => {
  const [selectedGroup, setSelectedGroup] = React.useState<GroupedEvents>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<Event>(null);
  const [selectedDay, setSelectedDay] = React.useState(MONDAY);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [activeFilters, setFilters] = React.useState<Filters>({
    seasons: [],
    divisions: [],
    teams: [],
    locations: [],
    sources: [],
  });

  const [rows, setRows] = React.useState(defaultRows);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const sunday = setDate(selectedDay, selectedDay.getDate() + 6);
  const [isExportModalOpen, setIsExportModalOpen] = React.useState(false);

  const { user } = React.useContext(AuthContext);
  const GA4data = { user: user && user.email, orgId: organizationId };

  React.useEffect(() => {
    const filters = getData().calendar?.filter;

    if (filters && Object.keys(filters).length > 0) {
      setFilters(filters);

      if (Object.values(filters).some((x) => !isNil(x) && x !== '')) {
        setFilterOpen(true);

        if (filters.seasons?.length) {
          getDivisionsAndTeams({
            variables: {
              organizationId: +organizationId,
              seasonIds: filters.seasons,
            },
          });
        }
      }
    }
  }, []);

  React.useEffect(() => {
    window.onscroll = () => {
      if (!headerRef?.current) return;

      window.pageYOffset - 58 > headerRef.current.offsetTop
        ? headerRef.current.classList.add('fixed')
        : headerRef.current.classList.remove('fixed');
    };
  }, [headerRef?.current?.offsetTop]);

  const { data: seasonsData } = useQuery<{
    calendarEventSeasons: CalendarEventSeason[];
  }>(EventSeasonsQuery, {
    variables: { organizationId: +organizationId },
    fetchPolicy: 'cache-first',
  });

  const [getDivisionsAndTeams, divisionsAndTeams] = useLazyQuery<{
    showCalendarEventSeasons: CalendarEventDivisions[];
  }>(DivisionsAndTeamsQuery, {
    fetchPolicy: 'cache-first',
  });

  const { data: calendarLocationsData } = useQuery<{
    calendarLocations: CalendarLocation[];
  }>(CalendarLocationsQuery, {
    variables: { organizationId: +organizationId },
    fetchPolicy: 'cache-first',
  });

  const { data, loading } = useQuery<{ calendarEvents: EventGQLType[] }>(
    CalendarEventsQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        organizationId,
        startDateTime: setHours(selectedDay, 0),
        endDateTime: setHours(setDate(sunday, sunday.getDate() + 1), 0),
        seasonIds: activeFilters.seasons,
        divisionIds: activeFilters.divisions,
        locationIds: activeFilters.locations,
        teamIds: activeFilters.teams,
        ...(activeFilters.sources?.length > 0 && {
          calendarSources: activeFilters.sources,
        }),
      },
    }
  );

  React.useEffect(() => {
    setRows(
      data?.calendarEvents.some((x) => new Date(x.startDateTime).getHours() < 6)
        ? fullDayRows
        : defaultRows
    );
  }, [data?.calendarEvents]);

  const SEASONS_FILTER = React.useMemo(() => {
    const seasons = seasonsData?.calendarEventSeasons || [];

    return PaginatedTable.SelectFilter(
      'seasons',
      'All Programs',
      seasons.map((x) => ({ label: x.fullName, value: x.id }))
    );
  }, [seasonsData]);

  const DIVISIONS_FILTER = React.useMemo(() => {
    const seasons = divisionsAndTeams.data?.showCalendarEventSeasons || [];
    const disabled = !activeFilters.seasons?.length;

    return PaginatedTable.SelectFilter(
      'divisions',
      'All Divisions',
      seasons.flatMap((x) =>
        x.divisions.map((division) => ({
          label: `${division.name} (${[x.program?.name, x.name]
            .filter((x) => x)
            .join(' ')})`,
          value: division.id,
        }))
      ),
      undefined,
      undefined,
      disabled,
      'Please select at least one program in the program filter'
    );
  }, [divisionsAndTeams.data, activeFilters.seasons]);

  const TEAMS_FILTER = React.useMemo(() => {
    const seasons = divisionsAndTeams.data?.showCalendarEventSeasons || [];
    const disabled = !activeFilters.seasons?.length;

    return PaginatedTable.SelectFilter(
      'teams',
      'All Teams',
      seasons.flatMap((season) =>
        season.teams.map((team) => ({
          label: `${team.name} (${[season.program?.name, season.name]
            .filter((x) => x)
            .join(' ')})`,
          value: team.id,
        }))
      ),
      undefined,
      undefined,
      disabled,
      'Please select at least one program in the program filter'
    );
  }, [divisionsAndTeams.data, activeFilters.seasons]);

  const LOCATIONS_FILTER = React.useMemo(() => {
    const locations = calendarLocationsData?.calendarLocations || [];

    return PaginatedTable.SelectFilter(
      'locations',
      'All Locations',
      locations.map((x) => ({ label: x.fullName, value: x.id }))
    );
  }, [calendarLocationsData]);

  const EVENT_SOURCE_FILTER = React.useMemo(() => {
    const sourcesObject = [];

    for (const [key, value] of Object.entries(eventSources)) {
      sourcesObject.push({ label: value, value: key });
    }

    return PaginatedTable.SelectFilter('sources', 'All Sources', sourcesObject);
  }, []);

  const setActiveFilters = (filter) => {
    const filters = {
      ...filter,
      divisions: activeFilters.seasons?.every((x) =>
        filter.seasons?.includes(x)
      )
        ? filter.divisions
        : [],
      teams: activeFilters.seasons?.every((x) => filter.seasons?.includes(x))
        ? filter.teams
        : [],
    };

    setFilters(filters);
    storeCalendarPage(filters);

    if (filter.seasons?.length) {
      getDivisionsAndTeams({
        variables: {
          organizationId: +organizationId,
          seasonIds: filter.seasons,
        },
      });
    }
  };

  const goBack = () =>
    setSelectedDay(setDate(selectedDay, selectedDay.getDate() - 7));
  const goForward = () =>
    setSelectedDay(setDate(selectedDay, selectedDay.getDate() + 7));
  const week = getWeek(selectedDay);
  const events =
    data?.calendarEvents.map((x) => ({
      ...x,
      startDate: new Date(x.startDateTime),
      endDate: new Date(x.endDateTime),
    })) || [];
  const groupedEvents = groupEvents(events);

  return (
    <>
      <EventModal
        GA4data={GA4data}
        show={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent || null}
      />

      <GroupModal
        GA4data={GA4data}
        show={selectedGroup !== null}
        onClose={() => setSelectedGroup(null)}
        group={selectedGroup || null}
      />

      <div className="Grid u-spaceBottomLg">
        <h1
          className="u-size4of12 sui-font-bold"
          data-testid="calendar-page-header"
        >
          Calendar
        </h1>
        <div
          className="u-size2of12 u-flex u-flexJustifyEnd u-flexAlignItemsCenter"
          data-testid="calendar-date-header"
        >
          <h2>{getMonthHeader(week)}</h2>
        </div>
        <div className="u-size2of12 u-flex u-flexAlignItemsCenter">
          <Icon
            otherProps={{ onClick: goBack }}
            name="left"
            className="u-spaceSidesLg pointer"
          />
          <Button onClick={() => setSelectedDay(MONDAY)} type="link">
            Today
          </Button>
          <Icon
            otherProps={{ onClick: goForward }}
            name="right"
            className="u-spaceSidesLg pointer"
          />
        </div>

        <div className="u-size2of12 u-flex u-flexJustifyEnd">
          <Button
            onClick={() => {
              setFilterOpen(!filterOpen);
            }}
            mods="Button Button--primary u-spaceLeftSm"
            icon="wrench"
          >
            Filter
          </Button>
        </div>

        <div className="u-size2of12 u-flexJustifyEnd">
          <Button
            onClick={() => setIsExportModalOpen(true)}
            mods="Button u-spaceLeftSm"
            icon="import"
          >
            Export
          </Button>
        </div>
      </div>

      <CalendarExportModal
        title="Export calendar events"
        show={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={activeFilters}
        organizationId={organizationId}
      />

      <CalendarFilters
        show={filterOpen}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        filters={[
          SEASONS_FILTER,
          DIVISIONS_FILTER,
          TEAMS_FILTER,
          LOCATIONS_FILTER,
          EVENT_SOURCE_FILTER,
        ]}
      />

      <div id="calendar-table">
        <div className="calendar-header" ref={headerRef}>
          <div className="timezone-column">
            <div>
              <p className="u-colorGrey">All events in</p>
              <p className="u-textBold">{getAbbreviatedTimezone()}</p>
            </div>
          </div>
          {week.map((date) => (
            <HeaderColumn key={date.toDateString()} date={date} />
          ))}
        </div>

        {loading ? (
          <Loader text="Loading..." />
        ) : (
          <div className="calendar-body">
            {rows.map(({ hour, label, period }) => (
              <div key={`${hour}=${label}`} className="calendar-row">
                <div className="calendar-col">
                  <div className="row">
                    {label} {period}
                  </div>
                  <div className="row">
                    {label}:30 {period}
                  </div>
                </div>
                {week.map((date) => {
                  const dateTime = setHours(date, hour);
                  const getCardsByDate = getEventCards(
                    groupedEvents,
                    setSelectedEvent,
                    setSelectedGroup,
                    GA4data
                  );

                  return (
                    <div key={date.toDateString()} className="calendar-col">
                      <div className="row">
                        {getCardsByDate(setMinutes(dateTime, 0))}
                      </div>
                      <div className="row">
                        {getCardsByDate(setMinutes(dateTime, 30))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default OrgCalendar;
